import { useState, useRef, type MutableRefObject } from 'react';
import type React from 'react';
import classNames from 'classnames';

import toast from 'components/partials/toast/toast';
import Button from 'components/partials/button/button';
import Tooltip from 'components/partials/tooltip/tooltip';
import { twMerge } from 'tailwind-merge';
import FeedbackSection from './feedback-section';
import MessageParts from './message-parts';

import { getTestId } from 'helpers/test-helpers';
import { copyToClipboard } from 'helpers/copy-to-clipboard';

import { useApiSendFeedback } from '../../hooks/use-api-send-feedback';

import { resolveMessageParts } from '../../helpers/message-helpers';

import { FEEDBACK_TYPES } from '../../ai-assistant.constants';

import type { ChatMessage } from 'containers/admin/clients/client/tabs-components/ai-assistant/hooks/use-active-input-part';

import Icons from 'assets/icons';

interface MessageItemProps {
  message: ChatMessage & {
    content?: string;
    loading?: boolean;
  };
  oktaUserId: string;
  clientId: string | number;
  conversationId: string;
  feedbackCloseRef: MutableRefObject<(() => void) | null>;
  onRegenerateMessage?: (messageId: string) => void;
  isReadOnly?: boolean;
}

interface MessageAction {
  icon: React.JSX.Element;
  label: string;
  onClick: () => void;
  isDisabled: boolean;
  customClass?: string;
}

const MessageItem = ({
  message,
  oktaUserId,
  clientId,
  conversationId,
  feedbackCloseRef,
  onRegenerateMessage,
  isReadOnly,
}: MessageItemProps): React.JSX.Element => {
  const { content, isUser } = message;
  const actionsRef = useRef<HTMLDivElement>(null);
  const messageRootRef = useRef<HTMLDivElement>(null);

  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [feedbackType, setFeedbackType] = useState<string | null>(null);
  const [isFeedbackSectionOpen, setIsFeedbackSectionOpen] = useState(false);

  const { mutateAsync: sendFeedback, isPending: isSendingFeedback } =
    useApiSendFeedback({
      oktaUserId,
      clientId,
      conversationId,
      messageId: message.id,
    });

  if (message.loading) {
    return (
      <div className="animate-fade-in flex align-center gap-3 border border-gray-200 px-4 py-3 rounded-md bg-gray-50 w-fit">
        <Icons.Spinner customClass="w-5 h-5 animate-spin" />
        <p className="text-gray-600">{content}</p>
      </div>
    );
  }

  const handleRegenerateMessage = (): void => {
    onRegenerateMessage?.(message.id);
  };

  const handleCopy = async (): Promise<void> => {
    try {
      await copyToClipboard(content ?? '');
      toast.success({ title: 'Copied to clipboard' });
    } catch {
      toast.error({ title: 'Failed to copy' });
    }
  };

  const handleCloseFeedbackSection = (): void => {
    setFeedbackType(null);
    setIsFeedbackSectionOpen(false);
    if (feedbackCloseRef) feedbackCloseRef.current = null;
  };

  const handleSendFeedback = async (
    type: string | null,
    reason = ''
  ): Promise<void> => {
    await sendFeedback({ type, reason });

    if (type === FEEDBACK_TYPES.POSITIVE) {
      setIsLiked(true);
    } else if (type === FEEDBACK_TYPES.NEGATIVE) {
      setIsDisliked(true);
    }

    handleCloseFeedbackSection();
  };

  const handleOpenFeedbackSection = (type: string): void => {
    feedbackCloseRef?.current?.();
    feedbackCloseRef.current = () => {
      setIsFeedbackSectionOpen(false);
      feedbackCloseRef.current = null;
    };

    setFeedbackType(type);
    setIsFeedbackSectionOpen(true);
  };

  const handleCancelFeedback = (): void => {
    setFeedbackType(null);
    setIsLiked(false);
    setIsDisliked(false);
  };

  const actions: MessageAction[] = (
    [
      {
        icon: <Icons.Retry />,
        label: 'Retry',
        onClick: handleRegenerateMessage,
        isDisabled: isSendingFeedback || !!isReadOnly,
      },
      {
        icon: <Icons.Copy />,
        label: 'Copy',
        onClick: () => {
          void handleCopy();
        },
        isDisabled: isSendingFeedback || !!isReadOnly,
      },
      !isDisliked && {
        icon: isLiked ? <Icons.ThumbsUpFilled /> : <Icons.ThumbsUp />,
        label: isLiked ? 'Liked' : 'Like',
        onClick: isLiked
          ? handleCancelFeedback
          : () => handleOpenFeedbackSection(FEEDBACK_TYPES.POSITIVE),
        isDisabled: isSendingFeedback || !!isReadOnly,
      },
      !isLiked && {
        icon: isDisliked ? <Icons.ThumbsDownFilled /> : <Icons.ThumbsDown />,
        label: isDisliked ? 'Disliked' : 'Dislike',
        onClick: isDisliked
          ? handleCancelFeedback
          : () => handleOpenFeedbackSection(FEEDBACK_TYPES.NEGATIVE),
        isDisabled: isSendingFeedback || !!isReadOnly,
      },
    ] as Array<MessageAction | false>
  ).filter(Boolean) as MessageAction[];

  return (
    <div
      ref={messageRootRef}
      className={twMerge(
        'relative animate-fade-in flex flex-col gap-2 max-w-[700px] text-md mb-5 text-gray-900',
        isUser && 'rounded-md bg-gray-100 ml-auto px-2.5 py-1.5 w-fit',
        !isUser && 'bg-transparent mr-auto w-full'
      )}
    >
      {isUser ? (
        <p className="whitespace-pre-wrap break-words">{content}</p>
      ) : (
        <MessageParts parts={resolveMessageParts(message)} />
      )}

      {!isUser && (
        <div ref={actionsRef} className="flex w-fit">
          {actions.map((action) => {
            const dataTestId = getTestId({
              prefix: 'message-action',
              label: action.label,
              suffix: 'button',
            });

            return (
              <Tooltip
                key={action.label}
                content={action.label}
                position="bottom-start"
                enabled={!action.isDisabled}
              >
                <Button
                  variant="reserve-text"
                  className={classNames(
                    '!size-8 !p-2 !text-gray-500',
                    action.customClass
                  )}
                  onClick={action.onClick}
                  disabled={action.isDisabled}
                  aria-label={action.label}
                  data-testid={dataTestId}
                >
                  {action.icon}
                </Button>
              </Tooltip>
            );
          })}
        </div>
      )}

      {isFeedbackSectionOpen && (
        <FeedbackSection
          onClose={handleCloseFeedbackSection}
          onSendFeedback={handleSendFeedback}
          isSendingFeedback={isSendingFeedback}
          feedbackType={feedbackType}
          anchorRef={messageRootRef}
          triggerRef={actionsRef}
        />
      )}
    </div>
  );
};

export default MessageItem;
